import React, { useEffect, useState } from 'react';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import countries from './countries.js';
import { createRoot } from 'react-dom/client';
import submitWizard from './submitWizard';

import './multistep-form.scss';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function isJson(str) {
    try {
        JSON.parse(str);
    }
    catch (e) {
        return false;
    }
    return true;
}

const Wizard = ({ children, initialValues, onSubmit, toggle }) => {
    const [gclid, setGclid] = useState('');
    const [stepNumber, setStepNumber] = useState(0);
    const steps = React.Children.toArray(children);
    const [snapshot, setSnapshot] = useState(initialValues);
    const [submitted, setSubmitted] = useState(false);
    const step = steps[stepNumber];
    const totalSteps = steps.length;
    const isLastStep = stepNumber === totalSteps - 1;

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const gclidParam = urlParams.get('gclid');
        const gclidStorage = localStorage.getItem('gclid');
        if (gclidParam) {
            setGclid(gclidParam);
        }
        else if (gclidStorage) {
            if (isJson(gclidStorage)) {
                const gclidObject = JSON.parse(gclidStorage);
                setGclid(gclidObject.value);
            }
            else {
                setGclid(gclidStorage);
            }
        }
    }, [gclid]);

    const next = (values) => {
        setSnapshot(values);
        setStepNumber(Math.min(stepNumber + 1, totalSteps - 1));
    };

    const previous = (values) => {
        setSnapshot(values);
        setStepNumber(Math.max(stepNumber - 1, 0));
    };

    const handleSubmit = async (values, bag) => {
        if (step.props.onSubmit) {
            await step.props.onSubmit(values, bag);
        }
        if (isLastStep) {
            setSubmitted(true);
            return onSubmit(values, bag);
        } else {
            bag.setTouched({});
            next(values);
        }
    };

    return (
        <Formik
            initialValues={snapshot}
            onSubmit={handleSubmit}
            validationSchema={step.props.validationSchema}
        >
            {(formik) => (
                <div className="multistep-form__outter-container">
                    <div className={`multistep-form__inner-container`}>
                        <div className="multistep-form__header-container">
                            <p className="multistep-form__form-header">
                                Request a <span className="gold-accent">Quote</span>
                            </p>
                            <p className="multistep-form__form-tagline">
                                Answer a few quick questions, and we’ll be in touch with a quote.
                            </p>
                            <ul className="multistep-form__desktop-step-list">
                                <li
                                    className={`multistep-form__desktop-step-list-item ${stepNumber === 0
                                            ? 'multistep-form__desktop-step-list-item--active'
                                            : ''
                                        }`}
                                >
                                    <img
                                        className="multistep-form__desktop-step-check"
                                        src={
                                            stepNumber === 0
                                                ? 'https://assets-us-01.kc-usercontent.com/469992e5-7cbd-0032-ead4-f2db9237053a/958d7dc3-05a1-49cc-b88b-eaf96389be11/icon_circle_check_orange.png'
                                                : 'https://assets-us-01.kc-usercontent.com/469992e5-7cbd-0032-ead4-f2db9237053a/b11a04dd-0479-44d9-b1b3-f50497ba0fd0/icon_circle_check_green.png'
                                        }
                                        alt="step 1"
                                    />
                                    Step 1: About You
                                </li>
                                <li
                                    className={`multistep-form__desktop-step-list-item ${stepNumber === 1
                                            ? 'multistep-form__desktop-step-list-item--active'
                                            : ''
                                        }`}
                                >
                                    <img
                                        alt="Circle icon with checkmark"
                                        className="multistep-form__desktop-step-check"
                                        src={
                                            stepNumber === 0
                                                ? 'https://assets-us-01.kc-usercontent.com/469992e5-7cbd-0032-ead4-f2db9237053a/2628e1c6-8dee-4ff9-8880-107bb6de697c/icon_circle_check_white.png'
                                                : stepNumber === 1
                                                    ? 'https://assets-us-01.kc-usercontent.com/469992e5-7cbd-0032-ead4-f2db9237053a/958d7dc3-05a1-49cc-b88b-eaf96389be11/icon_circle_check_orange.png'
                                                    : 'https://assets-us-01.kc-usercontent.com/469992e5-7cbd-0032-ead4-f2db9237053a/b11a04dd-0479-44d9-b1b3-f50497ba0fd0/icon_circle_check_green.png'
                                        }
                                    />
                                    Step 2: How to Reach You
                                </li>
                                <li
                                    className={`multistep-form__desktop-step-list-item ${stepNumber === 2
                                            ? 'multistep-form__desktop-step-list-item--active'
                                            : ''
                                        }`}
                                >
                                    <img
                                        alt="Icon with checkmark"
                                        className="multistep-form__desktop-step-check"
                                        src={
                                            stepNumber === 0 || stepNumber === 1
                                                ? 'https://assets-us-01.kc-usercontent.com/469992e5-7cbd-0032-ead4-f2db9237053a/2628e1c6-8dee-4ff9-8880-107bb6de697c/icon_circle_check_white.png'
                                                : stepNumber === 2 && !submitted
                                                    ? 'https://assets-us-01.kc-usercontent.com/469992e5-7cbd-0032-ead4-f2db9237053a/958d7dc3-05a1-49cc-b88b-eaf96389be11/icon_circle_check_orange.png'
                                                    : 'https://assets-us-01.kc-usercontent.com/469992e5-7cbd-0032-ead4-f2db9237053a/b11a04dd-0479-44d9-b1b3-f50497ba0fd0/icon_circle_check_green.png'
                                        }
                                    />
                                    Step 3: Company Size
                                </li>
                            </ul>
                            <i
                                className="far fa-times multistep-form__form-close-icon"
                                onClick={toggle}
                            ></i>
                        </div>
                        <div className="multistep-form__form-body-container">
                            <i
                                className="far fa-times multistep-form__form-close-icon-desktop"
                                onClick={toggle}
                            ></i>
                            <div className="multistep-form__active-step-container">
                                <ul className="multistep-form__step-list">
                                    <li
                                        className={`multistep-form__step-item ${stepNumber === 0
                                                ? 'multistep-form__step-item--active'
                                                : ''
                                            }`}
                                    >
                                        Step 1
                                    </li>
                                    <li
                                        className={`multistep-form__step-item ${stepNumber === 1
                                                ? 'multistep-form__step-item--active'
                                                : ''
                                            }`}
                                    >
                                        Step 2
                                    </li>
                                    <li
                                        className={`multistep-form__step-item ${stepNumber === 2 && !submitted
                                                ? 'multistep-form__step-item--active'
                                                : ''
                                            }`}
                                    >
                                        Step 3
                                    </li>
                                </ul>
                            </div>
                            <div className="multistep-form__form-body">
                                <Form
                                    className={`multistep-form__main-form ${submitted ? 'd-none' : ''
                                        }`}
                                >
                                    {step}
                                    <input type="hidden" id="gclid_wizard" name="gclid_field" value={gclid} />
                                    <div className="multistep-form__navigation-container">
                                        <button
                                            className={`btn DDIPrimaryOutlineButton large ${stepNumber === 0 ? 'disabled grey' : 'orange'
                                                }`}
                                            type="button"
                                            onClick={() => previous(formik.values)}
                                        >
                                            Back
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={formik.isSubmitting}
                                            className="btn DDIPrimaryOutlineButton large orange"
                                        >
                                            {isLastStep ? 'Submit ' : 'Next'}
                                            {isLastStep ? (
                                                <i className="far fa-arrow-right"></i>
                                            ) : null}
                                        </button>
                                    </div>
                                </Form>
                                <div
                                    className={`multistep-form__success-container ${submitted ? 'd-block' : 'd-none'
                                        }`}
                                >
                                    <p className="multistep-form__thank-you-tagline">Thank you!</p>
                                    <p className="multistep-form__thank-you-header">We can’t wait to talk to you!</p>
                                    <p className="multistep-form__thank-you-body">
                                        Thanks for sharing a little bit about your leadership development project. We’ll call you within one business day to share estimated pricing.  
                                    </p>
                                    <button
                                        className="multistep-form-initiator__button btn DDIPrimaryOutlineButton orange"
                                        onClick={toggle}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Formik>
    );
};

function FormInitiatior({ formOpen, toggle, dismissed, toggleDimissed }) {
    if (!dismissed) {
        return (
            <div className="multistep-form-initiator">
                <i className="far fa-times multistep-form-initiator__dismiss" onClick={toggleDimissed}></i>
                <div className="multistep-form-initiator__content">
                    <p className="multistep-form-initiator__header">
                        Get Pricing for Leadership Solutions
                    </p>
                    <p className="multistep-form-initiator__tagline">
                        Answer a few questions to get pricing options for your organization{' '}
                    </p>
                </div>
                <a
                    className="multistep-form-initiator__button btn__base"
                    href="/lp/request-quote"
                >
                    Request Quote
                </a>
            </div>
        );
    }
    else {
        return (
            <div className="multistep-form-initiator dismissed">
                <a className="multistep-form-initiator__button btn__base" href="/lp/request-quote">Request Quote</a>
            </div>
        )
    }
}

function FormOverlay({ formOpen, toggle }) {
    return (
        <div
            style={{ display: formOpen ? 'block' : 'none' }}
            className="multistep-form-overlay"
            onClick={toggle}
        ></div>
    );
}

const WizardStep = ({ children }) => children;

const MultistepForm = () => {
    const postData = async (data) => {
        submitWizard(data);
    };
    const [formOpen, setFormOpen] = useState(false);
    const [dismissed, setDimissed] = useState(false);
    function toggleForm(e) {
        e.preventDefault();
        setFormOpen(!formOpen);
    }

    function toggleDismissed(e) {
        e.preventDefault();
        setDimissed(!dismissed);
    }

    return (
        <div>
            <div
                className={`multistep-form ${formOpen ? 'multistep-form--active' : ''}`}
            >
                <Wizard
                    formOpen={formOpen}
                    initialValues={{
                        firstName: '',
                        lastName: '',
                        country: '',
                        jobTitle: '',
                        companyName: '',
                        email: '',
                        businessPhone: '',
                        numberOfLeaders: '',
                        numberOfEmployees: '',
                        GDPR: false,
                    }}
                    onSubmit={async (values) => {
                        sleep(300).then(() => {
                            postData(values);
                        });
                    }}
                    toggle={toggleForm}
                >
                    <WizardStep
                        validationSchema={Yup.object({
                            firstName: Yup.string().matches(/^[a-zA-Z]+$/, ' ').min(2, ' ').required(' '),
                            lastName: Yup.string().matches(/^[a-zA-Z]+$/, ' ').min(2, ' ').required(' '),
                            country: Yup.string().oneOf(countries).required(' '),
                            jobTitle: Yup.string().min(2, ' ').required(' '),
                        })}
                    >
                        <p className="multistep-form__step-number">Step 1:</p>
                        <p className="multistep-form__step-tagline">Tell Us About You</p>
                        <div className="form-group">
                            <ErrorMessage
                                className="multistep-form__error"
                                component="div"
                                name="firstName"
                            />
                            <label htmlFor="firstName" className="multistep-form__label">
                                First Name
                            </label>
                            <Field
                                autoComplete="given name"
                                component="input"
                                id="firstName"
                                name="firstName"
                                placeholder="First Name"
                                type="text"
                                className="form-control multistep-form__input-field"
                                helperText=""
                            ></Field>
                        </div>
                        <div className="form-group">
                            <ErrorMessage
                                className="multistep-form__error"
                                component="div"
                                name="lastName"
                            />
                            <label htmlFor="lastName" className="multistep-form__label">
                                Last Name
                            </label>
                            <Field
                                autoComplete="family-name"
                                component="input"
                                id="lastName"
                                name="lastName"
                                placeholder="Last Name"
                                type="text"
                                className="form-control multistep-form__input-field"
                            />
                        </div>
                        <div className="form-group">
                            <ErrorMessage
                                className="multistep-form__error"
                                component="div"
                                name="country"
                            />
                            <label htmlFor="country" className="multistep-form__label">
                                Country/Region
                            </label>
                            <Field
                                className="form-control multistep-form__input-field"
                                name="country"
                                id="country"
                                as="select"
                            >
                                <option value="">Select a Country/Region</option>
                                {countries.map((country) => (
                                    <option key={country} value={country}>{country}</option>
                                ))}
                            </Field>
                        </div>
                        <div className="form-group">
                            <ErrorMessage
                                className="multistep-form__error"
                                component="div"
                                name="jobTitle"
                            />
                            <label htmlFor="jobTitle" className="multistep-form__label">
                                Job Title
                            </label>
                            <Field
                                autoComplete="jobTitle"
                                component="input"
                                id="jobTitle"
                                name="jobTitle"
                                placeholder="Enter your Job Title"
                                type="text"
                                className="form-control multistep-form__input-field"
                            />
                        </div>
                    </WizardStep>
                    <WizardStep
                        onSubmit={() => console.log('Step2 onSubmit')}
                        validationSchema={Yup.object({
                            companyName: Yup.string().min(2, ' ').required(' '),
                            email: Yup.string().matches(/(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?!gmail\.com|yahoo\.com|cox\.net|msn\.com|rocketmail\.com|verizon\.net|hotmail\.com|live\.com|aol\.com|comcast\.net|outlook\.com|att\.com|att\.net|zoominternet\.net|ymail\.com|me\.com|icloud\.com|mac\.com)(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/,'Invalid email address').required(' '),
                            businessPhone: Yup.string().matches(/^(?:(?:\(?(?:00|\+)([1-4]\d\d|[1-9]\d*)\)?)[\-\.\ \\\/]?)?((?:\(?\d{1,}\)?[\-\.\ \\\/]?){0,})(?:[\-\.\ \\\/]?(?:#|ext\.?|extension|x)[\-\.\ \\\/]?(\d+))?$/igm, ' ').required(' '),
                        })}
                    >
                        <p className="multistep-form__step-number">Step 2:</p>
                        <p className="multistep-form__step-tagline">How Should We Reach You?</p>
                        <div className="form-group">
                            <ErrorMessage
                                className="multistep-form__error"
                                component="div"
                                name="companyName"
                            />
                            <label htmlFor="companyName" className="multistep-form__label">
                                Company Name
                            </label>
                            <Field
                                autoComplete="companyName"
                                component="input"
                                id="companyName"
                                name="companyName"
                                placeholder="Enter your Company Name"
                                type="text"
                                className="form-control multistep-form__input-field"
                            />
                        </div>
                        <div className="form-group">
                            <ErrorMessage
                                className="multistep-form__error"
                                component="div"
                                name="email"
                            />
                            <label htmlFor="email" className="multistep-form__label">
                                Company Email
                            </label>
                            <Field
                                autoComplete="email"
                                component="input"
                                id="email"
                                name="email"
                                placeholder="company@email.com"
                                type="text"
                                className="form-control multistep-form__input-field"
                            />
                        </div>
                        <div className="form-group">
                            <ErrorMessage
                                className="multistep-form__error"
                                component="div"
                                name="businessPhone"
                            />
                            <label htmlFor="businessPhone" className="multistep-form__label">
                                Business Phone
                            </label>
                            <Field
                                autoComplete="companyName"
                                component="input"
                                id="businessPhone"
                                name="businessPhone"
                                placeholder="Business Phone"
                                type="text"
                                className="form-control multistep-form__input-field"
                            />
                        </div>
                    </WizardStep>
                    <WizardStep
                        onSubmit={() => console.log('Step3 onSubmit')}
                        validationSchema={Yup.object({
                            numberOfLeaders: Yup.number().typeError(' ').positive(' ').integer(' ').required(' '),
                            numberOfEmployees: Yup.number().typeError(' ').positive(' ').integer(' ').required(' '),
                            GDPR: Yup.bool().oneOf([true], ' ').required(' '),
                        })}
                    >
                        <p className="multistep-form__step-number">Step 3:</p>
                        <p className="multistep-form__step-tagline">Your Best Estimate: Company Size</p>
                        <div className="form-group">
                            <ErrorMessage
                                className="multistep-form__error"
                                component="div"
                                name="numberOfLeaders"
                            />
                            <label
                                htmlFor="numberOfLeaders"
                                className="multistep-form__label"
                            >
                                Number of Leaders
                            </label>
                            <Field
                                autoComplete="numberOfLeaders"
                                component="input"
                                id="numberOfLeaders"
                                name="numberOfLeaders"
                                placeholder="ex. 100"
                                type="text"
                                className="form-control multistep-form__input-field"
                            />
                        </div>
                        <div className="form-group">
                            <ErrorMessage
                                className="multistep-form__error"
                                component="div"
                                name="numberOfEmployees"
                            />
                            <label
                                htmlFor="numberOfEmployees"
                                className="multistep-form__label"
                            >
                                Number of Employees
                            </label>
                            <Field
                                autoComplete="numberOfEmployees"
                                component="input"
                                id="numberOfEmployees"
                                name="numberOfEmployees"
                                placeholder="ex. 1000"
                                type="text"
                                className="form-control multistep-form__input-field"
                            />
                        </div>
                        <div className="form-group form-check">
                            <ErrorMessage
                                className="multistep-form__error"
                                component="div"
                                name='GDPR'
                            />
                            <Field 
                                type="checkbox" 
                                name="GDPR" 
                                id="GDPR" 
                                className="multistep-form__input-field form-check-input multistep-form__gdpr-checkbox"
                            />
                            <label 
                                htmlFor="GDPR" 
                                className="form-check-label multistep-form__label form-text font-weight-normal"
                            >
                                I agree that DDI may call me and use my personal data for marketing and research purposes. See our privacy policy for more details or to revoke your consent at any time.
                            </label>
                        </div>
                    </WizardStep>
                </Wizard>
            </div>
            <FormInitiatior dismissed={dismissed} toggleDimissed={toggleDismissed} formOpen={formOpen} toggle={toggleForm}></FormInitiatior>
            <FormOverlay formOpen={formOpen} toggle={toggleForm}></FormOverlay>
        </div>
    );
};

//export default MultistepForm;
let wizardContainer = document.createElement('div');
wizardContainer.setAttribute('id', 'wizardConatiner');
document.getElementsByTagName('body')[0].appendChild(wizardContainer);
const root = createRoot(wizardContainer);
root.render(<MultistepForm></MultistepForm>);